import { cloneDeep } from 'lodash-es';
import { createRequiredSportFilter, resetRequiredSportFilter } from '@/helpers/sportFilter';
import { CustomLeagueItem } from '@/models/customLeaguePage';
import { createCustomLeaguesPageHeaders } from '@/pages/system/customLeagues/CustomLeaguesPageHeaders';
import { isRequestCanceled } from '@/services/baseService';
import { customLeagueService } from '@/services/customLeagueService';
import { createLoadingGenerationTracker } from '@/stores/composables/createLoadingGenerationTracker';
import { createStorePersistConfig } from '@/stores/composables/createStorePersistConfig';
import { useFilterPanelState } from '@/stores/composables/useFilterPanelState';
import { useFilterPersistence } from '@/stores/composables/useFilterPersistence';
import { IPageOptions } from '@/types';
import type { ICustomLeagueFilters, ICustomLeagueUpdateResponse } from '@/types/customLeaguePage';
import type { DataTableHeader } from '@/types/vuetify';

const defaultSortBy = [{ key: 'name', order: 'asc' as const }];

function createCustomLeagueStoreSetup(storeId: string) {
    // --- state ---
    const items = shallowRef<CustomLeagueItem[]>([]);
    const total = ref<number>(0);
    const loading = ref(false);
    const loadGeneration = createLoadingGenerationTracker();

    // --- filters ---
    const filters = reactive<ICustomLeagueFilters>({
        sportId: createRequiredSportFilter(),
        customLeagueName: {
            value: null,
            title: 'customLeagues.name',
            allowSaveState: true,
        },
        customLeagueGender: {
            value: null,
            title: 'customLeagues.gender',
            allowSaveState: true,
        },
    });

    // --- filter panel ---
    const { hiddenFilters, visibleFiltersMobile, filterOrder, availableFilters, useButtonForFiltersApply, saveFiltersState, visibleFilters } =
        useFilterPanelState(filters);

    // --- options ---
    const options = ref<IPageOptions>({
        itemsPerPage: 100,
        page: 1,
        rowsPerPageItems: [100, 200, 500],
        sortBy: [...defaultSortBy],
    });

    // --- reset ---
    function resetFilters() {
        resetRequiredSportFilter(filters.sportId);
        filters.customLeagueName.value = null;
        filters.customLeagueGender.value = null;
    }

    // --- table ---
    const headers = computed<DataTableHeader[]>(() => createCustomLeaguesPageHeaders());

    const availableColumns = computed(() => headers.value.filter((header) => header.title));

    const hiddenColumns = ref<string[]>([]);

    const tableHeaders = computed<DataTableHeader[]>(() =>
        headers.value.filter((header) => header.key && (!hiddenColumns.value.includes(header.key) || !header.title) && !header.hidden)
    );

    // --- persistence ---
    const { reloadFiltersFromStorage } = useFilterPersistence({
        storeId,
        primaryStoreId: 'customLeagues',
        filters,
        saveFiltersState,
        resetFilters,
    });

    // --- actions ---
    async function loadData() {
        const loadId = loadGeneration.next();
        loading.value = true;
        try {
            const params = {
                filters: cloneDeep(filters),
                options: {
                    sortBy: options.value.sortBy,
                    rowsPerPageItems: options.value.rowsPerPageItems,
                    page: options.value.page,
                    itemsPerPage: options.value.itemsPerPage,
                },
                requiredData: [],
                requiredDictionaries: ['Country', 'AuditTable'],
            };

            const response = await customLeagueService.list(params);

            items.value = (response.data.rows || []).map((row) => new CustomLeagueItem(row));
            total.value = response.data.total ?? 0;
        } catch (e) {
            if (isRequestCanceled(e)) return;
            throw e;
        } finally {
            if (loadGeneration.isLatest(loadId)) {
                loading.value = false;
            }
        }
    }

    function updateOptions(value: Partial<IPageOptions>) {
        if (value.page !== undefined && value.page !== options.value.page) {
            options.value.page = value.page;
            loadData();
        }
        if (value.itemsPerPage !== undefined && value.itemsPerPage !== options.value.itemsPerPage) {
            options.value.itemsPerPage = value.itemsPerPage;
            options.value.page = 1;
            loadData();
        }
    }

    function clearData() {
        items.value = [];
        total.value = 0;
        options.value.page = 1;
        loading.value = false;
    }

    function mergeMutationResponse(response: { data: ICustomLeagueUpdateResponse }) {
        if (response.data.success === false) return;
        const row = response.data.row;
        if (!row) return;

        const idx = items.value.findIndex((i) => i.id === row.id);
        if (idx !== -1) {
            items.value[idx] = new CustomLeagueItem(row);
        } else {
            items.value = [new CustomLeagueItem(row), ...items.value];
            total.value += 1;
        }
        triggerRef(items);
    }

    async function patchBoolField(item: CustomLeagueItem, modelKey: 'fixing', value: 0 | 1, apiField: string) {
        const oldValue = item[modelKey];
        item[modelKey] = value;
        triggerRef(items);

        try {
            const response = await customLeagueService.update({ rows: [{ id: item.id, [apiField]: value }] });
            mergeMutationResponse(response);
            if (!response.data.row && response.data.success === false) {
                item[modelKey] = oldValue;
                triggerRef(items);
            }
        } catch {
            item[modelKey] = oldValue;
            triggerRef(items);
        }
    }

    // --- return ---
    return {
        storeId,
        items,
        total,
        filters,
        hiddenFilters,
        visibleFiltersMobile,
        filterOrder,
        availableFilters,
        useButtonForFiltersApply,
        saveFiltersState,
        visibleFilters,
        resetFilters,
        reloadFiltersFromStorage,
        headers,
        tableHeaders,
        availableColumns,
        hiddenColumns,
        loading,
        options,
        loadData,
        updateOptions,
        clearData,
        patchBoolField,
        mergeMutationResponse,
        hasAdd: true,
    };
}

export function useCustomLeagueStore(storeId: string) {
    const useStore = defineStore(storeId, () => createCustomLeagueStoreSetup(storeId), {
        persist: createStorePersistConfig(storeId, 'customLeagues', defaultSortBy),
    });
    return useStore();
}

export type CustomLeagueStoreSetup = ReturnType<typeof useCustomLeagueStore>;
